/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'

import {
    Box,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import NestedMobileMenu from '../nested-mobile-menu/index'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

import {styleConfigChakraKeys} from '../../constants'
import LoginAction from '../login-action'
import MobileMenuItem from './partials/mobile-menu-item'
import {LanguageSelector} from '../language-selector/LanguageSelector'
import Link from '../link'
import {navLinkAnimation} from '../../theme/components/project/drawer-menu'
/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({header, isOpen, onClose = noop, paddingTop = 40}) => {
    const menuItems = header?.menu?.items ?? []
    const administrativesData = header?.administratives ?? []
    const styles = useMultiStyleConfig(styleConfigChakraKeys.drawerMenu)
    const [ariaBusy, setAriaBusy] = useState('true')

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    const LinkActions = {
        Account: () => <LoginAction itemLength={menuItems.length} />,
        'Language Selector': () => <LanguageSelector />,
        'Book service': (item) => (
            <Link
                {...item}
                onLinkClick={() => {
                    onClose()
                }}
                buttonStyles={{
                    ...styles.mobileNavLink,
                    backgroundColor: 'white',
                    background: 'white',
                    padding: '0px',
                    animation: `${navLinkAnimation} 0.1s ease-in-out calc((var(--index) + 1) * 80ms) forwards`
                }}
                style={{'--index': 3}}
                marginLeft="auto"
                textAlign="right"
                sx={{
                    '-webkit-tap-highlight-color': 'transparent'
                }}
            />
        ),
        Link: (item) => <Link {...item} />,
        default: (item) => <Link {...item} />
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            variant="megaNavModal"
        >
            <ModalContent
                {...styles.modalContent}
                paddingTop={`${paddingTop}px`}
            >
                {/* Main Content */}
                <ModalBody
                    {...styles.modalBody}
                    paddingBottom="0"
                    sx={{'.mobile-mega-nav': {display: 'flex'}}}
                >
                    <div
                        id="category-nav"
                        aria-live="polite"
                        aria-busy={ariaBusy}
                        aria-atomic="true"
                        className="mobile-mega-nav"
                    >
                        {/* Category Navigation */}
                        {header ? (
                            <Flex flexFlow="column" margin="auto" gap="30px">
                                <Box>
                                    <NestedMobileMenu
                                        isNavOpen={isOpen}
                                        items={menuItems?.map(
                                            ({sys, fields}) => ({
                                                ...fields,
                                                sys
                                            })
                                        )}
                                        closeDrawer={onClose}
                                    />
                                </Box>
                                <Box>
                                    {administrativesData?.map((item) => {
                                        const actionType =
                                            item?.actionType || 'default'
                                        return LinkActions[actionType](item)
                                    })}
                                </Box>
                            </Flex>
                        ) : (
                            <Box width="80%" margin="auto">
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <Flex marginTop="34px" gap="20px">
                                    <Skeleton
                                        mb="2"
                                        height="200px"
                                        width="50%"
                                    />
                                    <Skeleton
                                        mb="2"
                                        height="200px"
                                        width="50%"
                                    />
                                </Flex>
                            </Box>
                        )}
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    header: PropTypes.object,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Function called when the drawer logo is clicked.
     */
    onLogoClick: PropTypes.func,
    paddingTop: PropTypes.number
}

export default DrawerMenu
